'use client';

import clsx from 'clsx';
import React from 'react';

/**
 * DS selection controls — design_docs/The-Realest-Design-Skill.md §7.3.
 *
 * | Control  | Size        | States                          |
 * | Checkbox | 16 x 16     | Empty · Hover · Filled          |
 * | Radio    | 16 x 16     | Unselected · Hover · Selected   |
 * | Toggle   | 29.33 x 16  | Unselected · Selected           |
 *
 * The checkbox is square with a hard check — §1.1's "nothing has a corner
 * radius" applies. The radio is round because it is an inherently round
 * object, and the toggle is explicitly called out as "the only pill-shaped
 * control in the system", so both are exceptions by design rather than drift.
 *
 * All three are rendered from a real input so they stay keyboard- and
 * screen-reader-native; the visual is a sibling driven by peer-checked.
 */

/**
 * The native control is sr-only and the visual is a sibling, but
 * @tailwindcss/forms still hands bare checkboxes and radios its #2563eb
 * accent — which it paints as the checked fill through currentColor. Reset it
 * onto the DS ink so no stray blue exists in the tree at all.
 */
const SR_INPUT = 'peer sr-only accent-surface-600 text-surface-600';

/**
 * The 16px box a checkbox or radio draws, and the check inside it.
 *
 * Exported because `SelectionRow` in Input.tsx draws the same control and had
 * restated it — the two then drifted, and only one of them picked up the
 * master's white ground and lift (396:7113, checkbox 48:145). One definition,
 * so a change to the control cannot land in only half the app.
 *
 * The ground matters: the box used to be transparent, so it took the colour
 * of whatever row it sat on and all but vanished against a selected row's
 * surface-200 fill.
 *
 * Checked differs by control and is the only part that does — a checkbox
 * fills, a radio keeps its ground and shows a dot.
 */
export const controlBoxClass = (control: 'checkbox' | 'radio' = 'checkbox') =>
    clsx(
        'flex size-[16px] shrink-0 items-center justify-center border transition-colors',
        'bg-surface-000 shadow-ds-s',
        'border-surface-300 group-hover:border-surface-400',
        'peer-disabled:border-surface-300 peer-disabled:bg-surface-200',
        control === 'radio'
            ? 'rounded-full peer-checked:border-surface-600 peer-checked:[&>span]:opacity-100'
            : 'rounded-ds peer-checked:border-surface-600 peer-checked:bg-surface-600 peer-checked:[&>svg]:opacity-100'
    );

/** Hard check, not a rounded tick. Hidden until the box is checked. */
export const ControlTick = () => (
    <svg viewBox="0 0 10 8" className="h-[8px] w-[10px] opacity-0 transition-opacity" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.6" className="text-ink-100" />
    </svg>
);

/** The radio's dot. Same deal. */
export const ControlDot = () => (
    <span className="size-[8px] rounded-full bg-surface-600 opacity-0 transition-opacity" />
);

const focusRing =
    'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-surface-600';

type ControlProps = {
    label?: React.ReactNode;
    className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>;

const Wrapper = ({
    children,
    label,
    className,
    disabled
}: {
    children: React.ReactNode;
    label?: React.ReactNode;
    className?: string;
    disabled?: boolean;
}) => (
    <label
        className={clsx(
            'text-body-400 group inline-flex items-center gap-[8px] text-ink-400',
            disabled ? 'cursor-not-allowed text-surface-400' : 'cursor-pointer',
            className
        )}
    >
        {children}
        {label}
    </label>
);

export const Checkbox = ({ label, className, ...rest }: ControlProps) => (
    <Wrapper label={label} className={className} disabled={rest.disabled}>
        <input type="checkbox" className={SR_INPUT} {...rest} />
        <span aria-hidden className={clsx(controlBoxClass('checkbox'), focusRing)}>
            <ControlTick />
        </span>
    </Wrapper>
);

export const Radio = ({ label, className, ...rest }: ControlProps) => (
    <Wrapper label={label} className={className} disabled={rest.disabled}>
        <input type="radio" className={SR_INPUT} {...rest} />
        <span aria-hidden className={clsx(controlBoxClass('radio'), focusRing)}>
            <ControlDot />
        </span>
    </Wrapper>
);

export const Toggle = ({ label, className, ...rest }: ControlProps) => (
    <Wrapper label={label} className={className} disabled={rest.disabled}>
        <input type="checkbox" role="switch" className={SR_INPUT} {...rest} />
        <span
            aria-hidden
            className={clsx(
                'relative flex h-[16px] w-[29.33px] shrink-0 items-center rounded-full transition-colors',
                'bg-surface-300 peer-checked:bg-surface-600',
                'peer-checked:[&>span]:translate-x-[13.33px]',
                'peer-disabled:bg-surface-200',
                focusRing
            )}
        >
            <span className="absolute left-[2px] size-[12px] rounded-full bg-surface-000 transition-transform" />
        </span>
    </Wrapper>
);
